import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  // Redirect already-authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = "Email address is required";
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData?.password?.trim()) {
      newErrors.password = "Password is required";
    } else if (formData?.password?.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (isLocked) {
      setErrors({
        general: "Account temporarily locked due to multiple failed attempts. Please try again in 15 minutes."
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signInWithPassword({
        email: formData?.email?.trim(),
        password: formData?.password,
      });

      if (authError) {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        if (newAttempts >= 5) {
          setIsLocked(true);
          setErrors({
            general: "Account locked due to multiple failed login attempts. Please try again in 15 minutes or contact support."
          });
        } else {
          setErrors({
            general: `Invalid email or password. ${5 - newAttempts} attempt(s) remaining.`
          });
        }
        return;
      }

      // Verify the user is an admin in parent_profiles
      const { data: profileData, error: profileError } = await supabase
        ?.from('parent_profiles')
        ?.select('is_admin')
        ?.eq('id', authData?.user?.id)
        ?.single();

      if (profileError || !profileData?.is_admin) {
        // Sign out the non-admin user
        await supabase?.auth?.signOut();
        setErrors({
          general: "Access denied. This portal is restricted to administrators only. Please contact support@kidquest.com if you believe this is an error."
        });
        return;
      }

      // Admin verified — store session info and navigate
      if (formData?.rememberMe) {
        localStorage.setItem('kidquest-remember', 'true');
        localStorage.setItem('kidquest-email', formData?.email);
      }

      navigate('/dashboard');
    } catch (err) {
      setErrors({
        general: "A network error occurred. Please check your connection and try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password recovery functionality will be implemented. Please contact your system administrator at support@kidquest.com for assistance.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFBF7] via-[#FFF0E8] to-[#FFE4D0] flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        <div className="hidden lg:flex flex-col items-center justify-center p-12 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl">
          <div className="w-32 h-32 bg-gradient-to-br from-[#FF6B2C] to-[#FFC83D] rounded-3xl flex items-center justify-center mb-8 shadow-lg">
            <Icon name="Rocket" size={64} color="white" />
          </div>
          <h1 className="text-4xl font-bold text-[#17263C] mb-4 text-center">
            KidQuest CMS
          </h1>
          <p className="text-lg text-[#6E7D91] text-center mb-8 max-w-md">
            Comprehensive Content Management System for creating, organizing, and publishing educational content for the KidQuest mobile application.
          </p>
          <div className="grid grid-cols-3 gap-6 w-full max-w-md">
            <div className="flex flex-col items-center p-4 bg-[#FFF0E8] rounded-xl">
              <Icon name="FileText" size={32} className="text-[#FF6B2C] mb-2" />
              <span className="text-sm font-medium text-[#17263C]">Content</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-[#EBF6FE] rounded-xl">
              <Icon name="Image" size={32} className="text-[#42A5E8] mb-2" />
              <span className="text-sm font-medium text-[#17263C]">Media</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-[#E8F9F4] rounded-xl">
              <Icon name="BarChart3" size={32} className="text-[#35C99A] mb-2" />
              <span className="text-sm font-medium text-[#17263C]">Analytics</span>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10">
            <div className="flex lg:hidden items-center justify-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B2C] to-[#FFC83D] rounded-2xl flex items-center justify-center">
                <Icon name="Rocket" size={32} color="white" />
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#17263C] mb-2">
                Welcome Back
              </h2>
              <p className="text-[#6E7D91]">
                Sign in to access your CMS dashboard
              </p>
            </div>

            {errors?.general && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 whitespace-pre-line">{errors?.general}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="admin@kidquest.com"
                value={formData?.email}
                onChange={handleInputChange}
                error={errors?.email}
                required
                disabled={isLocked}
              />

              <Input
                label="Password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLocked}
              />

              <div className="flex items-center justify-between">
                <Checkbox
                  label="Remember me"
                  checked={formData?.rememberMe}
                  onChange={handleCheckboxChange}
                  disabled={isLocked}
                />
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm font-medium text-[#FF6B2C] hover:text-[#e55a1f] transition-colors duration-200"
                  disabled={isLocked}
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isLoading}
                disabled={isLocked}
                iconName="LogIn"
                iconPosition="right"
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Icon name="Shield" size={16} className="text-green-600" />
                  <span>SSL Secured</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Icon name="Lock" size={16} className="text-green-600" />
                  <span>256-bit Encryption</span>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                Need help? Contact{' '}
                <a href="mailto:support@kidquest.com" className="text-blue-600 hover:text-blue-700 font-medium">
                  support@kidquest.com
                </a>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              &copy; {new Date()?.getFullYear()} KidQuest. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;